import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { driversApi } from '../services/api';
import { useTenantContextStore } from '../store/tenant-context.store';
import { useHasModule } from '../store/modules';
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  Search,
  Phone,
  Mail,
  CreditCard,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Key,
  Fingerprint,
  Gauge,
  Eye,
  EyeOff,
  RefreshCw,
  ClipboardList,
} from 'lucide-react';

// Render a translated "... {{name}} ..." string with the name bolded, WITHOUT
// dangerouslySetInnerHTML — the name is rendered as a React child and is therefore escaped.
function boldNameNode(template: string, name: string) {
  const SENTINEL = '__TF_NAME__'; // visible placeholder; never present in translation text
  const parts = template.split(SENTINEL);
  return (
    <>
      {parts[0]}
      <strong>{name}</strong>
      {parts.slice(1).join(SENTINEL)}
    </>
  );
}

interface Driver {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  licenseNumber: string | null;
  licenseExpiry: string | null;
  iButtonId: string | null;
  rfidId: string | null;
  userId: string | null;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  } | null;
  isActive: boolean;
  missionsEnabled: boolean;
  missionsDisabledAt: string | null;
  createdAt: string;
}

interface DriverStats {
  totalDrivers: number;
  activeDrivers: number;
  driversWithLicenseExpiringSoon: number;
  driversWithExpiredLicense: number;
  missionSeatsUsed: number;
  missionSeatsSold: number;
}

export default function DriversPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';
  const fieldServiceEnabled = useHasModule('FIELD_SERVICE');
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editDriver, setEditDriver] = useState<Driver | null>(null);
  const [deleteDriver, setDeleteDriver] = useState<Driver | null>(null);
  const [togglingMissionsId, setTogglingMissionsId] = useState<string | null>(null);
  const [disableMissionsDriver, setDisableMissionsDriver] = useState<Driver | null>(null);

  const SEAT_COOLDOWN_DAYS = 30;
  const getCooldownDaysLeft = (driver: Driver): number => {
    if (!driver.missionsDisabledAt) return 0;
    const disabledAt = new Date(driver.missionsDisabledAt).getTime();
    const cooldownEndsAt = disabledAt + SEAT_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
    const diff = cooldownEndsAt - Date.now();
    return diff > 0 ? Math.ceil(diff / (24 * 60 * 60 * 1000)) : 0;
  };

  // Fetch drivers
  const { data: driversData, isLoading } = useQuery({
    queryKey: ['drivers', search, tenantKey],
    queryFn: () => driversApi.list({ search: search || undefined }),
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['drivers-stats', tenantKey],
    queryFn: () => driversApi.getStats(),
  });

  const drivers: Driver[] = driversData?.data?.data || [];
  const stats: DriverStats = statsData?.data || {
    totalDrivers: 0,
    activeDrivers: 0,
    driversWithLicenseExpiringSoon: 0,
    driversWithExpiredLicense: 0,
    missionSeatsUsed: 0,
    missionSeatsSold: 0,
  };

  const toggleMissionsMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      driversApi.setMissionsEnabled(id, enabled),
    onMutate: ({ id }) => setTogglingMissionsId(id),
    onSettled: () => setTogglingMissionsId(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      queryClient.invalidateQueries({ queryKey: ['drivers-stats'] });
    },
    onError: (err: any) => {
      alert(err?.response?.data?.error || t('drivers.missions_modify_error'));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => driversApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      queryClient.invalidateQueries({ queryKey: ['drivers-stats'] });
      setDeleteDriver(null);
    },
  });

  const getLicenseStatus = (expiry: string | null) => {
    if (!expiry) return null;
    const expiryDate = new Date(expiry);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: t('drivers.license_expired'), color: 'red' };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring', label: t('drivers.license_expires_in_days', { days: daysUntilExpiry }), color: 'orange' };
    }
    return { status: 'valid', label: t('drivers.license_valid'), color: 'green' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('drivers.title')}</h1>
          <p className="text-gray-500 mt-1">{t('drivers.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/drivers/scores"
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 border border-gray-200 text-gray-600 hover:text-gray-900 hover:border-gray-300 transition-colors"
          >
            <Gauge className="h-5 w-5" />
            <span className="hidden sm:inline">{t('drivers.scoring_link')}</span>
          </Link>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-glow flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            {t('drivers.new_driver')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Users className="h-6 w-6" />}
          label={t('drivers.stat_total')}
          value={stats.totalDrivers}
          color="cyan"
        />
        <StatCard
          icon={<CheckCircle2 className="h-6 w-6" />}
          label={t('drivers.stat_active')}
          value={stats.activeDrivers}
          color="green"
        />
        <StatCard
          icon={<AlertTriangle className="h-6 w-6" />}
          label={t('drivers.stat_license_expiring')}
          value={stats.driversWithLicenseExpiringSoon}
          color="orange"
        />
        <StatCard
          icon={<XCircle className="h-6 w-6" />}
          label={t('drivers.stat_license_expired')}
          value={stats.driversWithExpiredLicense}
          color="red"
        />
      </div>

      {/* Mission seats banner — only if FIELD_SERVICE module is enabled */}
      {fieldServiceEnabled && (
        <div className="glass-card p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-500/20 text-primary-500">
              <ClipboardList className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{t('drivers.missions_module_title')}</p>
              <p className="text-xs text-gray-500">
                {stats.missionSeatsSold === 0
                  ? t('drivers.missions_no_seats')
                  : stats.missionSeatsUsed >= stats.missionSeatsSold
                    ? t('drivers.missions_quota_full')
                    : t('drivers.missions_seats_left', { count: stats.missionSeatsSold - stats.missionSeatsUsed })}
              </p>
            </div>
          </div>
          <div
            className={`px-4 py-2 rounded-lg text-sm font-bold ${
              stats.missionSeatsUsed > stats.missionSeatsSold
                ? 'bg-red-100 text-red-700'
                : stats.missionSeatsUsed === stats.missionSeatsSold && stats.missionSeatsSold > 0
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-gray-100 text-gray-700'
            }`}
          >
            {t('drivers.missions_seats_used', { used: stats.missionSeatsUsed, total: stats.missionSeatsSold })}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="glass-card p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('drivers.search_placeholder')}
            className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 pl-12"
          />
        </div>
      </div>

      {/* Drivers list */}
      <div className="glass-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-400" />
          </div>
        ) : drivers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">
              {search ? t('drivers.no_match') : t('drivers.no_driver_yet')}
            </p>
            {!search && (
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-4 text-primary-400 hover:text-primary-300 text-sm"
              >
                {t('drivers.add_driver')}
              </button>
            )}
          </div>
        ) : (
          <table className="table-dark">
            <thead>
              <tr>
                <th>{t('drivers.th_driver')}</th>
                <th className="hidden md:table-cell">{t('drivers.th_contact')}</th>
                <th className="hidden md:table-cell">{t('drivers.th_license')}</th>
                <th className="hidden md:table-cell">{t('drivers.th_identification')}</th>
                <th>{t('drivers.th_status')}</th>
                {fieldServiceEnabled && <th>{t('drivers.th_missions')}</th>}
                <th className="text-right">{t('drivers.th_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((driver) => {
                const licenseStatus = getLicenseStatus(driver.licenseExpiry);
                return (
                  <tr key={driver.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                          <span className="text-primary-400 font-semibold">
                            {driver.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{driver.name}</p>
                          <p className="text-xs text-gray-500 md:hidden">
                            {driver.phone || driver.email || ''}
                            {driver.isActive ? ` · ${t('drivers.active')}` : ` · ${t('drivers.inactive')}`}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="hidden md:table-cell">
                      <div className="space-y-1">
                        {driver.phone && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Phone className="h-3.5 w-3.5" />
                            <span className="text-sm">{driver.phone}</span>
                          </div>
                        )}
                        {driver.email && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="h-3.5 w-3.5" />
                            <span className="text-sm">{driver.email}</span>
                          </div>
                        )}
                        {!driver.phone && !driver.email && (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </div>
                    </td>
                    <td className="hidden md:table-cell">
                      {driver.licenseNumber ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <CreditCard className="h-3.5 w-3.5 text-gray-500" />
                            <span className="text-sm text-gray-900">{driver.licenseNumber}</span>
                          </div>
                          {licenseStatus && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              licenseStatus.color === 'green'
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : licenseStatus.color === 'orange'
                                ? 'bg-orange-500/20 text-orange-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {licenseStatus.label}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="hidden md:table-cell">
                      <div className="space-y-1">
                        {driver.iButtonId && (
                          <div className="flex items-center gap-2">
                            <Key className="h-3.5 w-3.5 text-amber-400" />
                            <span className="text-xs text-gray-600 font-mono">{driver.iButtonId}</span>
                          </div>
                        )}
                        {driver.rfidId && (
                          <div className="flex items-center gap-2">
                            <Fingerprint className="h-3.5 w-3.5 text-purple-400" />
                            <span className="text-xs text-gray-600 font-mono">{driver.rfidId}</span>
                          </div>
                        )}
                        {!driver.iButtonId && !driver.rfidId && (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {driver.isActive ? (
                        <span className="badge-online">{t('drivers.active')}</span>
                      ) : (
                        <span className="badge-offline">{t('drivers.inactive')}</span>
                      )}
                    </td>
                    {fieldServiceEnabled && (
                      <td>
                        {(() => {
                          const isBusy = togglingMissionsId === driver.id;
                          const checked = driver.missionsEnabled;
                          const cooldownDaysLeft = checked ? 0 : getCooldownDaysLeft(driver);
                          const quotaFull =
                            !checked &&
                            stats.missionSeatsSold > 0 &&
                            stats.missionSeatsUsed >= stats.missionSeatsSold;
                          const disabled =
                            isBusy ||
                            (!checked && stats.missionSeatsSold === 0) ||
                            quotaFull ||
                            cooldownDaysLeft > 0;
                          const title = cooldownDaysLeft > 0
                            ? t('drivers.tooltip_blocked_days', { count: cooldownDaysLeft })
                            : !stats.missionSeatsSold
                              ? t('drivers.tooltip_no_seats')
                              : quotaFull
                                ? t('drivers.tooltip_quota_full')
                                : checked
                                  ? t('drivers.tooltip_remove_access')
                                  : t('drivers.tooltip_grant_access');
                          const onClick = () => {
                            if (checked) {
                              // Disabling → open the confirm modal explaining the 30-day lock
                              setDisableMissionsDriver(driver);
                            } else {
                              toggleMissionsMutation.mutate({ id: driver.id, enabled: true });
                            }
                          };
                          return (
                            <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={onClick}
                              disabled={disabled}
                              title={title}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                checked ? 'bg-primary-500' : 'bg-gray-300'
                              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  checked ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                              {isBusy && (
                                <Loader2 className="absolute inset-0 m-auto h-3 w-3 animate-spin text-white" />
                              )}
                            </button>
                            {cooldownDaysLeft > 0 && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium whitespace-nowrap"
                                title={t('drivers.cooldown_tooltip')}
                              >
                                {t('drivers.blocked_days', { days: cooldownDaysLeft })}
                              </span>
                            )}
                            </div>
                          );
                        })()}
                      </td>
                    )}
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditDriver(driver)}
                          className="p-2 rounded-lg hover:bg-white/10 text-gray-500 hover:text-white transition-colors"
                          title={t('drivers.edit')}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteDriver(driver)}
                          className="p-2 rounded-lg hover:bg-red-500/20 text-gray-500 hover:text-red-400 transition-colors"
                          title={t('drivers.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editDriver) && (
        <DriverModal
          driver={editDriver}
          onClose={() => {
            setShowAddModal(false);
            setEditDriver(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['drivers'] });
            queryClient.invalidateQueries({ queryKey: ['drivers-stats'] });
            setShowAddModal(false);
            setEditDriver(null);
          }}
        />
      )}

      {/* Disable Missions seat confirmation — warns about the 30-day cooldown */}
      {disableMissionsDriver && (
        <div className="modal-overlay" onClick={() => setDisableMissionsDriver(null)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-lg bg-amber-100 text-amber-700 shrink-0">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('drivers.remove_seat_title')}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {boldNameNode(t('drivers.driver_label', { name: '__TF_NAME__' }), disableMissionsDriver.name)}
                </p>
              </div>
            </div>
            <div className="space-y-3 text-sm text-gray-700 mb-6">
              <p>
                {boldNameNode(t('drivers.remove_seat_intro', { name: '__TF_NAME__' }), disableMissionsDriver.name)}
              </p>
              <ul className="list-disc pl-5 space-y-1 text-gray-600">
                <li>{t('drivers.remove_seat_li_1')}</li>
                <li>{t('drivers.remove_seat_li_2')}</li>
              </ul>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                <p className="font-semibold mb-1">{t('drivers.remove_seat_warning_title')}</p>
                <p className="text-xs">{t('drivers.remove_seat_warning_body')}</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDisableMissionsDriver(null)}
                className="btn-secondary"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  toggleMissionsMutation.mutate(
                    { id: disableMissionsDriver.id, enabled: false },
                    { onSettled: () => setDisableMissionsDriver(null) },
                  );
                }}
                disabled={toggleMissionsMutation.isPending}
                className="btn-danger flex items-center gap-2"
              >
                {toggleMissionsMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('drivers.remove_seat_button')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteDriver && (
        <div className="modal-overlay" onClick={() => setDeleteDriver(null)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('drivers.delete_title')}</h3>
            <p className="text-gray-500 mb-6">
              {t('drivers.delete_confirm', { name: deleteDriver.name })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteDriver(null)}
                className="btn-secondary"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteDriver.id)}
                disabled={deleteMutation.isPending}
                className="btn-danger flex items-center gap-2"
              >
                {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: 'cyan' | 'green' | 'orange' | 'red';
}) {
  const colorClasses = {
    cyan: 'text-cyan-400 bg-cyan-500/20',
    green: 'text-emerald-400 bg-emerald-500/20',
    orange: 'text-orange-400 bg-orange-500/20',
    red: 'text-red-400 bg-red-500/20',
  };

  return (
    <div className="glass-card p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

// Driver Modal Component
function DriverModal({
  driver,
  onClose,
  onSuccess,
}: {
  driver?: Driver | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const isEditing = !!driver;
  const [formData, setFormData] = useState({
    name: driver?.name || '',
    phone: driver?.phone || '',
    email: driver?.email || '',
    password: '',
    licenseNumber: driver?.licenseNumber || '',
    licenseExpiry: driver?.licenseExpiry ? driver.licenseExpiry.split('T')[0] : '',
    iButtonId: driver?.iButtonId || '',
    rfidId: driver?.rfidId || '',
    isActive: driver?.isActive ?? true,
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (data: typeof formData) => {
      const payload: Record<string, unknown> = {
        name: data.name,
        phone: data.phone || undefined,
        email: data.email || undefined,
        licenseNumber: data.licenseNumber || undefined,
        licenseExpiry: data.licenseExpiry ? new Date(data.licenseExpiry).toISOString() : undefined,
        iButtonId: data.iButtonId || undefined,
        rfidId: data.rfidId || undefined,
      };
      // Password is only sent on create and only if non-empty
      if (!isEditing && data.password) payload.password = data.password;
      // isActive only makes sense on update
      if (isEditing) payload.isActive = data.isActive;
      return driver
        ? driversApi.update(driver.id, payload)
        : driversApi.create(payload);
    },
    onSuccess,
    onError: (err: any) => {
      const data = err?.response?.data;
      if (data?.details && typeof data.details === 'object') {
        const msgs: string[] = [];
        for (const [field, messages] of Object.entries(data.details)) {
          if (Array.isArray(messages)) msgs.push(`${field}: ${(messages as string[]).join(', ')}`);
        }
        setError(msgs.join(' · ') || data.error || t('drivers.generic_error'));
        return;
      }
      setError(data?.error || t('drivers.generic_error'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    mutation.mutate(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {driver ? t('drivers.modal_edit_title') : t('drivers.modal_create_title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-500 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              {t('drivers.field_full_name')}
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
              placeholder={t('drivers.field_full_name_placeholder')}
              required
            />
          </div>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                {t('drivers.field_phone')}
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                placeholder={t('drivers.field_phone_placeholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                {t('drivers.field_email')}
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                placeholder={t('drivers.field_email_placeholder')}
              />
            </div>
          </div>

          {/* Password (create only) */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                {t('drivers.field_password')}
                <span className="ml-2 text-xs font-normal text-gray-400">
                  {t('drivers.field_password_optional_help')}
                </span>
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                placeholder={t('drivers.field_password_placeholder')}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('drivers.field_password_explainer')}
              </p>
            </div>
          )}

          {/* Set/reset password (edit only) */}
          {isEditing && driver && (
            <PasswordResetSection driverId={driver.id} driverEmail={driver.email} />
          )}

          {/* License */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                {t('drivers.field_license_number')}
              </label>
              <input
                type="text"
                value={formData.licenseNumber}
                onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                placeholder="123456789"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                {t('drivers.field_license_expiry')}
              </label>
              <input
                type="date"
                value={formData.licenseExpiry}
                onChange={(e) => setFormData({ ...formData, licenseExpiry: e.target.value })}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
              />
            </div>
          </div>

          {/* Identification */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                <div className="flex items-center gap-2">
                  <Key className="h-4 w-4 text-amber-400" />
                  iButton ID
                </div>
              </label>
              <input
                type="text"
                value={formData.iButtonId}
                onChange={(e) => setFormData({ ...formData, iButtonId: e.target.value })}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 font-mono"
                placeholder="01-XXXXXXXXXXXX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                <div className="flex items-center gap-2">
                  <Fingerprint className="h-4 w-4 text-purple-400" />
                  RFID
                </div>
              </label>
              <input
                type="text"
                value={formData.rfidId}
                onChange={(e) => setFormData({ ...formData, rfidId: e.target.value })}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 font-mono"
                placeholder="XXXXXXXXXXXX"
              />
            </div>
          </div>

          {/* Status (only for edit) */}
          {driver && (
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 bg-white text-primary-500 focus:ring-primary-500/20"
              />
              <label htmlFor="isActive" className="text-sm text-gray-600">
                {t('drivers.field_active')}
              </label>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">
              {t('drivers.cancel')}
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || !formData.name}
              className="btn-glow flex items-center gap-2"
            >
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {driver ? t('drivers.submit_save') : t('drivers.submit_create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PasswordResetSection — inline collapsible panel in the driver edit modal
// that lets an admin define or reset the driver's login password. Used mostly
// to give real credentials to drivers migrated with placeholder emails.
// ---------------------------------------------------------------------------

function PasswordResetSection({
  driverId,
  driverEmail,
}: {
  driverId: string;
  driverEmail: string | null;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const isPlaceholderEmail = !driverEmail; // null when backend strips placeholder
  const [newEmail, setNewEmail] = useState('');
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const mutation = useMutation({
    mutationFn: () => driversApi.setPassword(driverId, password, isPlaceholderEmail ? newEmail : undefined),
    onSuccess: () => {
      setResult({ type: 'success', message: t('drivers.password_set_success') });
      setPassword('');
      setNewEmail('');
      setShowPassword(false);
    },
    onError: (err: any) => {
      const data = err?.response?.data;
      const msg = data?.error || data?.message || t('drivers.password_set_error');
      setResult({ type: 'error', message: msg });
    },
  });

  const generate = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let p = '';
    // Ensure we hit each required category
    p += 'A';
    p += 'b';
    p += '1';
    for (let i = 0; i < 9; i++) p += chars.charAt(Math.floor(Math.random() * chars.length));
    // Shuffle
    p = p.split('').sort(() => Math.random() - 0.5).join('');
    setPassword(p);
    setShowPassword(true);
  };

  const validate = (): string | null => {
    if (isPlaceholderEmail && !newEmail.trim()) return t('drivers.validate_email_required');
    if (isPlaceholderEmail && !/^\S+@\S+\.\S+$/.test(newEmail)) return t('drivers.validate_email_invalid');
    if (password.length < 8) return t('drivers.validate_password_min');
    if (!/[A-Z]/.test(password)) return t('drivers.validate_password_upper');
    if (!/[a-z]/.test(password)) return t('drivers.validate_password_lower');
    if (!/[0-9]/.test(password)) return t('drivers.validate_password_digit');
    return null;
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResult(null);
    const err = validate();
    if (err) {
      setResult({ type: 'error', message: err });
      return;
    }
    mutation.mutate();
  };

  return (
    <div className="border border-gray-200 rounded-lg">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 rounded-lg"
      >
        <div className="flex items-center gap-2">
          <Key className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-900">
            {open ? t('drivers.password_section_close') : t('drivers.password_section_open')}
          </span>
          {isPlaceholderEmail && !open && (
            <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
              {t('drivers.password_placeholder_email_action')}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-400">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="p-4 pt-2 border-t border-gray-200 space-y-3" onClick={(e) => e.stopPropagation()}>
          {isPlaceholderEmail && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('drivers.field_account_email')}
                <span className="text-amber-600 ml-1">{t('drivers.field_account_email_help')}</span>
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={t('drivers.field_account_email_placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-700">{t('drivers.new_password_label')}</label>
              <button
                type="button"
                onClick={generate}
                className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <RefreshCw className="h-3 w-3" />
                {t('drivers.new_password_generate')}
              </button>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('drivers.field_password_placeholder')}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded text-sm font-mono"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {result && (
            <div
              className={`text-xs p-2 rounded ${
                result.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}
            >
              {result.message}
            </div>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={mutation.isPending}
            className="w-full py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white rounded text-sm font-medium flex items-center justify-center gap-2"
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
            {t('drivers.set_password_button')}
          </button>
        </div>
      )}
    </div>
  );
}
